import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { pageTitle } from '../../../util/pageTitles';
import moment from 'moment';
import { formatWorkDuration, getWorkMinutesPerDay } from '../../../util/dates';
import { Tooltip } from 'react-bootstrap';
import _ from 'lodash';

import NoData from '../../../components/NoData/NoData';
import { overviewIsLoading, setPageName } from './../../App/AppActions';

import Filter from '../../../components/Filter/Filter';
import { DatePicker } from '../../../components/Filter/components';
import { setChart, updateSelectedOptions, saveConfig } from '../../../components/Filter/FilterActions';
import { getData} from '../OverviewActions';
import { getSelectionValues } from '../../../util/filterUtils';
import { trackTeamDashboardView } from '../../../util/mixpanel';

import CFDChart from '../components/CFD';
import CTSPChart from '../components/CTSP';
import CTBChart from '../components/CTB';
import CTHChart from '../components/CTH';
import CTAChart from '../components/CTA';
import TRCChart from '../components/TRC';
import Widget from '../components/Widget';
import { ALL_WIDGETS, ALL_WIDGETS_SMALL, ALL_WIDGET_IDS } from '../OverviewConstants';
import { GridStackManager } from '../../../util/gridStackUtils';

// Import Styles
import './../Overview.scss';

class Overview extends Component {
  constructor(props) {
    super(props);
    this.tooltipParentRef = React.createRef();
    this.gridRef = React.createRef();
    
    // Initialize widget configuration based on saved order or default
    const defaultConfig = window.innerWidth > 1500 ? ALL_WIDGETS : ALL_WIDGETS_SMALL;
    
    this.gridManager = new GridStackManager(this.gridRef, {
      cellHeight: 342,
      widgetConfig: defaultConfig,
      widgetIdPrefix: 'widget-',
    });
    
    // Restore configuration from saved widget order if available
    if (props.selectedWidgets && props.selectedWidgets.length > 0) {
      const savedData = {
        widgets: props.selectedWidgets,
        layoutData: props.widgetLayout // This would come from your saved config
      };
      this.gridManager.restoreFromSavedOrder(savedData, defaultConfig);
    }
    
    this.gridManager.setDefaultWidgetOrder(ALL_WIDGET_IDS);
    this.grid = null;
    this.resizeTimeout = null;
    this.isUpdatingWidgets = false;

    this.state = {
      oct: 0,
      ct: 0,
      wip: 0,
      wipAvgAge: 0,
      showFlowMetrics: false,
      showProcessImprovement: false,
      customizeMode: false,
      tempSelectedWidgets: null,
      tempLayoutPositions: null,
      isGridReady: false
    };

    props.dispatch(setPageName('Overview'));
  }

  componentDidMount() {
    if (this.props.dashboard && this.props.dashboard._id && this.props.dataIsLoading === false) {
      this.props.dispatch(setChart('overview'));

      trackTeamDashboardView('overview');
    }

    if (this.props.ctsp && this.props.ctsp.percentiles && this.props.ctsp.percentiles.length) {
      this.updateCycleTime();
    }

    if (this.props.cta && this.props.cta.series && this.props.cta.series.length) {
      this.updateAvgAge();
    }

    if (this.props.cfd && this.props.cfd.length) {
      this.updateWip();
    }

    this.loadGridStack().then(() => {
      this.forceLayoutUpdate(() => {
          this.handleResize(() => {
            setTimeout(() => {
              window.dispatchEvent(new Event('resize'));              
              this.setState({ isGridReady: true });
            }, 100); 
          });
      });
    });

    this.forceLayoutUpdate = this.forceLayoutUpdate.bind(this);
    this.handleResize = this.handleResize.bind(this);
    window.addEventListener('resize', this.handleResize);
  }

  forceLayoutUpdate = (callback) => {
    if (this.grid) {
      const availableWidgets = this.getAvailableWidgets();

      // During customization, use temp widgets; otherwise use props
      let selectedWidgets;
      if (this.state.customizeMode && this.state.tempSelectedWidgets) {
        selectedWidgets = this.state.tempSelectedWidgets.filter(widgetId => availableWidgets.includes(widgetId));
      } else {
        selectedWidgets = this.props.selectedWidgets && this.props.selectedWidgets.length > 0
          ? this.props.selectedWidgets.filter(widgetId => availableWidgets.includes(widgetId))
          : availableWidgets;
      }

      // In customize mode, use handleTempWidgetSelectionChange to preserve drag state
      // Otherwise use handleGoBackFromCustomize which sets static mode
      if (this.state.customizeMode) {
        this.gridManager.handleTempWidgetSelectionChange([...selectedWidgets]);
        this.forceUpdate();
        if (typeof callback === 'function') {
          callback();
        }
      } else {
        this.gridManager.handleGoBackFromCustomize(
          [...selectedWidgets],
          () => {
            this.forceUpdate();
            if (typeof callback === 'function') {
              callback();
            }
          }
        );
      }
    }
  };

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
    this.gridManager.destroy();
  }

  componentDidUpdate(prevProps, prevState) {
    this.isLoaded();
    if (
      (prevProps.dataHash != this.props.dataHash ||
        prevProps.hash != this.props.hash ||
        prevProps.excludeWeekends !== this.props.excludeWeekends ||
        prevProps.workStart !== this.props.workStart ||
        prevProps.workEnd !== this.props.workEnd ||
        !_.isEqual(prevProps.holidays, this.props.holidays) ||
        prevProps.enabledColors !== this.props.enabledColors) &&
      this.props.dataIsLoading === false
    ) {
      this.getData();
    }

    if (
      (prevProps.dataHash != this.props.dataHash ||
        prevProps.hash != this.props.hash ||
        prevProps.excludeWeekends !== this.props.excludeWeekends ||
        prevProps.workStart !== this.props.workStart ||
        prevProps.workEnd !== this.props.workEnd ||
        !_.isEqual(prevProps.holidays, this.props.holidays) ||
        prevProps.enabledColors !== this.props.enabledColors) &&
      this.props.overviewLoading
    ) {
      this.setState({ pendingUpdate: true });
    }

    if (
      prevProps.overviewLoading !== this.props.overviewLoading &&
      !this.props.overviewLoading &&
      this.state.pendingUpdate
    ) {
      this.setState({ pendingUpdate: false });
      this.getData();
    }

    if (prevProps.ctsp != this.props.ctsp) {
      this.updateCycleTime();
    }

    if (prevProps.cta != this.props.cta) {
      this.updateAvgAge();
    }

    if (prevProps.cfd != this.props.cfd) {
      this.updateWip();
    }

    if (!_.isEqual(prevProps.selectedWidgets, this.props.selectedWidgets)) {
      const availableWidgets = this.getAvailableWidgets();
      const newSelectedWidgets = this.props.selectedWidgets ? 
        this.props.selectedWidgets.filter(widgetId => availableWidgets.includes(widgetId)) : 
        availableWidgets;
      
      // Restore configuration from saved order if available
      if (this.props.selectedWidgets && this.props.selectedWidgets.length > 0) {
        const defaultConfig = window.innerWidth > 1500 ? ALL_WIDGETS : ALL_WIDGETS_SMALL;
        const savedData = {
          widgets: this.props.selectedWidgets,
          layoutData: this.props.widgetLayout
        };
        this.gridManager.restoreFromSavedOrder(savedData, defaultConfig);
      }
      
      this.isUpdatingWidgets = true;
      this.gridManager.handleGoBackFromCustomize(newSelectedWidgets, () => {
        this.forceUpdate();
        this.isUpdatingWidgets = false;
      });
    }
  }

  async loadGridStack() {
    this.grid = await this.gridManager.loadGridStack();
    this.applyInitialLayout();
  }

  applyInitialLayout = () => {
    if (this.grid) {
      this.forceLayoutUpdate();
    }
  };

  handleResize = (callback) => {
    // Don't interfere with widget selection operations
    if (this.isUpdatingWidgets) {
      if (typeof callback === 'function') {
        callback();
      }
      return;
    }

    if (this.grid && this.gridRef.current) {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        // Only update widget config if we don't have custom saved configuration
        if (!this.props.selectedWidgets || this.props.selectedWidgets.length === 0) {
          this.gridManager.setWidgetConfig(window.innerWidth > 1500 ? ALL_WIDGETS : ALL_WIDGETS_SMALL);
        }
        // Only trigger layout recalculation during actual window resize
        this.forceLayoutUpdate(() => {
          if (typeof callback === 'function') {
            callback();
          }
        });
      }, 150);
    } else if (typeof callback === 'function') {
      callback();
    }
  };

  updateCycleTime() {
    const eightyfifth = _.find(this.props.ctsp.percentiles, p => p.name == '85');
    if (eightyfifth) {
      this.setState({
        ct: eightyfifth.rawValue
      });
    }

    const overviewEightyFifth = _.find(this.props.ctsp.percentiles, p => p.name == '85');
    this.setState({
      oct: overviewEightyFifth ? overviewEightyFifth.rawValue : 0
    });
  }

  updateAvgAge() {
    const wipAvgAge = _(this.props.cta.series)
      .flatMap('data')
      .meanBy('cycleTimeInMinutes');

    this.setState({
      wipAvgAge
    });
  }

  updateWip() {
    let wip = 0;
    _(this.props.cfd)
      .filter(l => {
        return (
          l.listId !== 'done' &&
          this.props.dashboard.todo.indexOf(l.listId) === -1 &&
          this.props.dashboard.done.indexOf(l.listId) === -1
        );
      })
      .forEach(l => {
        let last = _.last(l.data);
        wip += last.daily;
      });

    this.setState({
      wip
    });
  }

  isLoaded() {
    if (
      this.props.overviewLoading &&
      !this.props.cfdLoading &&
      !this.props.cthLoading &&
      !this.props.ctspLoading &&
      !this.props.ctaLoading &&
      !this.props.thLoading &&
      !this.props.trcLoading &&
      !this.props.ctAnalysisLoading &&
      !this.props.ctbAnalysisLoading &&
      !this.props.wipAnalysisLoading &&
      !this.props.wipAgeAnalysisLoading &&
      !this.props.thAnalysisLoading &&
      !this.props.feAnalysisLoading
    ) {
      this.props.dispatch(overviewIsLoading(false));
    }
  }

  isWidgetVisible = (widgetId) => {
    const { queueLists, allQueueLists } = this.props;
    const showFlowEfficiency = getSelectionValues(queueLists, allQueueLists).length < allQueueLists.length;
    
    // Hide flow efficiency widget if not available
    if (widgetId === 'flow_efficiency_metric' && !showFlowEfficiency) {
      return false;
    }
    
    // Use temp state during customize mode
    const selectedWidgets = this.state.customizeMode 
      ? this.state.tempSelectedWidgets 
      : this.props.selectedWidgets;
    
    if (!selectedWidgets || selectedWidgets.length === 0) {
      return true; // Default to visible if no selection saved
    }
    
    if (Array.isArray(selectedWidgets)) {
      return selectedWidgets.includes(widgetId);
    }
    
    return true;
  };

  getData() {
    if (!this.props.dataIsLoading && !this.props.overviewLoading) {
      this.props.dispatch(overviewIsLoading(true));
      this.props.dispatch(getData());
    }
  }

  getAvailableWidgets = () => {
    const { queueLists, allQueueLists } = this.props;
    const showFlowEfficiency = getSelectionValues(queueLists, allQueueLists).length < allQueueLists.length;
    
    if (!showFlowEfficiency) {
      return ALL_WIDGET_IDS.filter(widgetId => widgetId !== 'flow_efficiency_metric');
    }
    return ALL_WIDGET_IDS;
  };

  handleWidgetSelectionChange = (selectedWidgets) => {
    this.forceUpdate();
  };  

  handleCustomizeViewClick = () => {
    const availableWidgets = this.getAvailableWidgets();
    const currentSelected = this.props.selectedWidgets || availableWidgets;
    const filteredSelected = currentSelected.filter(widgetId => availableWidgets.includes(widgetId));
    
    this.setState({
      customizeMode: true,
      tempSelectedWidgets: [...filteredSelected],
    }, () => {
      // Ensure grid is properly initialized before enabling customize mode
      if (!this.grid && this.gridManager.grid) {
        this.grid = this.gridManager.grid;
      }
      
      if (this.grid) {
        this.gridManager.handleCustomizeViewClick(this.state.tempSelectedWidgets);
      } else {
        // If grid isn't ready yet, wait for it
        this.loadGridStack().then(() => {
          this.gridManager.handleCustomizeViewClick(this.state.tempSelectedWidgets);
        });
      }
    });
  };

  handleGoBackFromCustomize = () => {
    const availableWidgets = this.getAvailableWidgets();
    const currentSelected = this.props.selectedWidgets && this.props.selectedWidgets.length > 0 
      ? this.props.selectedWidgets 
      : availableWidgets;
    const filteredSelected = currentSelected.filter(widgetId => availableWidgets.includes(widgetId));
    
    this.isUpdatingWidgets = true;
    this.setState({
      customizeMode: false,
      tempSelectedWidgets: null,
      tempLayoutPositions: null
    }, () => {
      this.gridManager.handleGoBackFromCustomize(
        [...filteredSelected],
        () => {
          this.forceUpdate();
          this.isUpdatingWidgets = false;
        }
      );
    });
  };

  handleApplyCustomization = () => {
    this.isUpdatingWidgets = true;
    const layoutResult = this.gridManager.handleApplyCustomization();
    
    console.log('Layout result:', layoutResult); // Debug log
    
    if (layoutResult.widgets && layoutResult.widgets.length > 0) {
      // Save both widget order and complete layout data
      this.props.dispatch(updateSelectedOptions('widgets', layoutResult.widgets, 'overview'));
      // Also save layout data if supported by your config system
      if (layoutResult.layoutData && layoutResult.layoutData.length > 0) {
        this.props.dispatch(updateSelectedOptions('widgetLayout', layoutResult.layoutData, 'overview'));
      }
      this.props.dispatch(saveConfig());
    }
    
    this.setState({
      customizeMode: false,
      tempSelectedWidgets: null,
      tempLayoutPositions: null
    }, () => {
      this.isUpdatingWidgets = false;
    });
  };

  handleTempWidgetSelectionChange = (selectedWidgets) => {
    this.setState({ tempSelectedWidgets: selectedWidgets }, () => {
      this.gridManager.handleTempWidgetSelectionChange(selectedWidgets);
    });
  };

  renderSystemMetrics() {
    let stats = _.first(this.props.cfdStats);

    let systemAvgArrivalRate = stats ? stats.avgArrivalRate : 0.0;
    let systemAvgThroughput = stats ? stats.avgThroughput : 0.0;
    let systemAvgDailyWIP = stats ? stats.avgDailyWIP : 0;
    let systemAvgCycleTime = formatWorkDuration(
      (stats ? stats.avgCycleTime : 0) * getWorkMinutesPerDay(this.props.excludeWeekendOpts, this.props.useWorkingHoursInCycleTimeDays),
      this.props.excludeWeekendOpts,
      this.props.timePrecision,
      this.props.useWorkingHoursInCycleTimeDays
    );

    return (
      <div className='info-box-content'>
        <div className='info-box-item'>
          <h4>{systemAvgArrivalRate.toFixed(2)}</h4>
          <p>Arrival Rate items/day</p>
        </div>
        <div className='info-box-item'>
          <h4>{systemAvgThroughput.toFixed(2)}</h4>
          <p>Throughput items/day</p>
        </div>
        <div className='info-box-item'>
          <h4>{systemAvgDailyWIP.toFixed(2)}</h4>
          <p>Daily WIP items</p>
        </div>
        <div className='info-box-item'>
          <h4>{systemAvgCycleTime}</h4>
          <p>Cycle Time</p>
        </div>
      </div>
    );
  }

  tooltipWidth = () => {
    return this.tooltipParentRef.current ? `${this.tooltipParentRef.current.offsetWidth - 5}px` : 'auto';
  }

  getWidgetContent = (widgetId) => {
    const { showFlowEfficiency } = this.props;
    switch (widgetId) {
      case 'cycle_time_metric':
        return (
          <div className="cell-content cell-justify-center">
            <div className='info-box-item'>
              <h4>{`${formatWorkDuration(this.state.oct, this.props.excludeWeekendOpts, this.props.timePrecision, this.props.useWorkingHoursInCycleTimeDays)} or less`}</h4>
              <p>were needed to complete</p>
            </div>
            <div className='info-box-item'>
              <h4>85%</h4>
              <p>work items</p>
            </div>
          </div>
        );
      case 'wip_metric':
        return (
          <div className='cell-content cell-justify-center'>
            <div className='info-box-item'>
              <h4>
                {this.state.wip} work item{this.state.wip != 1 ? 's' : ''}
              </h4>
              <p>{this.state.wip != 1 ? 'are' : 'is'} currently in progress</p>
            </div>
            <div className='info-box-item'>
              <h4>
                {formatWorkDuration(this.state.wipAvgAge, this.props.excludeWeekendOpts, this.props.timePrecision, this.props.useWorkingHoursInCycleTimeDays)}
              </h4>
              <p>WIP average age</p>
            </div>
          </div>
        );
      case 'flow_efficiency_metric':
        return (
          <div className='cell-content cell-justify-center '>
            <div className='info-box-item'>
              <h4>{this.props.avgFe.avgFe || '0.00'}%</h4>
              <p>Average Flow Efficiency</p>
            </div>
          </div>
        );
      case 'process_metrics':
        return (
          <div className='cell-content cell-justify-center'>
            {this.renderSystemMetrics()}
          </div>
        );
      case 'cfd_chart':
        return (
          <React.Fragment>
            {this.props.cfd.length > 0 &&
              <div className='cell-content'>
                <CFDChart id="Overview_CFD" className="chart" data={this.props.cfd} />
              </div>
            }
            <NoData
              className="cell-content overview_CFD"
              show={!this.props.cfd.length}
              loading={this.props.cfdLoading}
            />
          </React.Fragment>
        );
      case 'ctsp_chart':
        return (
          <React.Fragment>
            {(this.props.ctsp.series && this.props.ctsp.series.length > 0) &&
              <div className='cell-content'>
                <CTSPChart id="Overview_CTSP" className="chart" data={this.props.ctsp} />
              </div>
            }
            <NoData
              className="cell-content overview_CTSP"
              show={this.props.ctsp.series && !this.props.ctsp.series.length}
              loading={this.props.ctspLoading}
            />
          </React.Fragment>
        );
      case 'ctb_chart':
        return (
          <React.Fragment>
            {(this.props.ctb.breakdown && this.props.ctb.breakdown.length > 0) &&
              <div className='cell-content'>
                <CTBChart id="Overview_CTB" className="chart" data={this.props.ctb.breakdown} />
              </div>
            }
            <NoData
              className="cell-content overview_CTB"
              show={this.props.ctb.breakdown && !this.props.ctb.breakdown.length}
              loading={this.props.ctbLoading}
            />
          </React.Fragment>
        );
      case 'cth_chart':
        return (
          <React.Fragment>
            {(this.props.cth.series && this.props.cth.series.length > 0) &&
              <div className='cell-content'>
                <CTHChart id="Overview_CTH" className="chart" data={this.props.cth} />
              </div>
            }
            <NoData
              className="cell-content overview_CTH"
              show={this.props.cth.series && !this.props.cth.series.length}
              loading={this.props.cthLoading}
            />
          </React.Fragment>
        );
      case 'cta_chart':
        return (
          <React.Fragment>
            {(this.props.cta.series && this.props.cta.series.length > 0) &&
              <div className='cell-content'>
                <CTAChart id="Overview_CTA" className="chart" data={this.props.cta} />
              </div>
            }
            <NoData
              className="cell-content overview_CTA"
              show={this.props.cta.series && !this.props.cta.series.length}
              loading={this.props.ctaLoading}
            />
          </React.Fragment>
        );
      case 'trc_chart':
        return (
          <React.Fragment>
            {this.props.trc.length &&
              <div className='cell-content'>
                <TRCChart id="Overview_TRC" className="chart" data={this.props.trc} />
              </div>
            }
            <NoData
              className="cell-content overview_TRC"
              show={!this.props.trc.length}
              loading={this.props.trcLoading}
            />
          </React.Fragment>
        );
      default:
        return null;
    }
  }

  getWidgetProps = (widgetId, showFlowEfficiency) => {
    const { dashboard } = this.props;
    const widgetInfo = ALL_WIDGETS[widgetId];
    if (!widgetInfo) return null;

    const props = { ...widgetInfo };

    if (['cycle_time_metric', 'wip_metric', 'process_metrics'].includes(widgetId)) {
      props.w = showFlowEfficiency ? 3 : 4;
    }

    if (props.linkTo) {
      props.linkTo = `/dashboard/${dashboard._id}/${props.linkTo}`;
    }

    return props;
  }

  render() {
    const { queueLists, allQueueLists, selectedWidgets } = this.props;
    const { customizeMode, tempSelectedWidgets } = this.state;
    const showFlowEfficiency = getSelectionValues(queueLists, allQueueLists).length < allQueueLists.length;

    const totalActiveItems = !this.props.cta ? 0 : _.reduce(this.props.cta.series, (p, c) => {
      return p + (c.data && c.data.length || 0);
    }, 0);

    const totalCompletedItems = !this.props.ctsp ? 0 : _.reduce(this.props.ctsp.series, (p, c) => {
      return p + (c.data && c.data.length || 0);
    }, 0);

    const totalItems = totalActiveItems + totalCompletedItems;

    const availableWidgets = this.getAvailableWidgets();
    let widgetsToRender = selectedWidgets;

    if (customizeMode) {
      // In customize mode, we render all available widgets so they can be selected/deselected.
      widgetsToRender = availableWidgets;
    } else {
      if (!widgetsToRender || widgetsToRender.length === 0) {
        // Default to all available widgets when no selection is saved
        widgetsToRender = availableWidgets;      
      } else {
        // Filter out unavailable widgets from selected widgets
        widgetsToRender = widgetsToRender.filter(widgetId => availableWidgets.includes(widgetId));
      }
    }

    const tresholdsCalculationsTooltip = (
      <Tooltip id="tresholdsTooltip" className='tooltip_white calculate_info' style={{ width: this.tooltipWidth() }}>
        Your thresholds are set by analyzing a time-ordered sequence of data, using between 4 to 20 data points (with 20 as the default).
        <br></br>
        <br></br>
        To flag a low or high status for your flow metrics, we look for a signal—a significant deviation from your typical process behavior.
        <br></br>
        <br></br>
        When a signal is detected, the benchmarks are frozen, and no new data points are included. Once at least 4 new data points are available, they are used to establish a new baseline.
      </Tooltip>
    );

    return (
      <div className='background_gray overview_page'>
        <Helmet>
          <title>{pageTitle(`Overview${this.props.dashboard ? ' | ' + this.props.dashboard.name : ''}`)}</title>
        </Helmet>        

        <Filter 
          totalItems={totalItems} 
          isOverview 
          colors 
          disabledLists={['todo']} 
          onWidgetSelectionChange={this.handleWidgetSelectionChange}
          customizeMode={customizeMode}
          onCustomizeViewClick={this.handleCustomizeViewClick}
          onGoBackFromCustomize={this.handleGoBackFromCustomize}
          onApplyCustomization={this.handleApplyCustomization}
          onTempWidgetSelectionChange={this.handleTempWidgetSelectionChange}
          tempSelectedWidgets={tempSelectedWidgets}
          isProcessImprovement={false}
          selectedWidgets={selectedWidgets}
          availableWidgets={availableWidgets}
        />
        <div className='overview'>
          <div className='overview-header'>
            <div className='overview-header-right-elements' ref={this.tooltipParentRef}>
             <DatePicker />
            </div>
          </div>

          <React.Fragment>
            <div 
              className="grid-stack" 
              ref={this.gridRef}
              style={{ visibility: this.state.isGridReady ? 'visible' : 'hidden' }}
            >
              {widgetsToRender.filter(widgetId => {
                // Filter out flow efficiency widget when not available
                if (widgetId === 'flow_efficiency_metric' && !showFlowEfficiency) {
                  return false;
                }
                return true;
              }).map(widgetId => {
                const widgetProps = this.getWidgetProps(widgetId, showFlowEfficiency);
                if (!widgetProps) return null;

                const isVisible = this.isWidgetVisible(widgetId);
                const style = (this.state.customizeMode && !isVisible) ? { display: 'none' } : {};

                return (
                  <Widget
                    key={widgetId}
                    id={`widget-${widgetId}`}
                    title={widgetProps.title}
                    w={widgetProps.w}
                    h={widgetProps.h}
                    isMetric={widgetProps.isMetric}
                    customizeMode={this.state.customizeMode}
                    showFlowEfficiency={showFlowEfficiency}
                    style={style}
                    linkTo={widgetProps.linkTo}
                  >
                    {this.getWidgetContent(widgetId)}
                  </Widget>
                );
              })}
            </div>
          </React.Fragment>
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => {
  return {
    app: state.app.app,
    user: state.auth.user,
    enabledColors: state.filters.enabledColors,
    timePrecision: state.filters.timePrecision,
    excludeWeekends: state.filters.excludeWeekends,
    workStart: state.filters.workStart,
    workEnd: state.filters.workEnd,
    holidays: state.filters.holidays,
    excludeWeekendOpts: {
      type: state.filters.excludeWeekends,
      workStart: state.filters.workStart,
      workEnd: state.filters.workEnd,
      holidays: state.filters.holidays
    },
    queueLists: state.filters.overview.selectedQueueLists,
    allQueueLists: state.filters.allQueueLists,
    hash: state.filters.overview.hash,

    overviewLoading: state.app.overviewLoading,
    dataIsLoading: state.app.isLoading,
    dataHash: state.app.dataHash,

    dashboard: state.dashboards.selected,

    overviewType: state.overview.overviewType,
    cfd: state.overview.cfd,
    cfdLoading: state.overview.cfdLoading,
    cfdStats: state.overview.cfdStats,
    ctsp: state.overview.ctsp,
    ctspLoading: state.overview.ctspLoading,
    ctb: state.overview.ctb,
    ctbLoading: state.overview.ctbLoading,
    cth: state.overview.cth,
    cthLoading: state.overview.cthLoading,
    cta: state.overview.cta,
    ctaLoading: state.overview.ctaLoading,
    trc: state.overview.trc,
    trcLoading: state.overview.trcLoading,
    avgFe: state.overview.avgFe,
    selectedWidgets: state.filters.overview.selectedWidgets,
    widgetLayout: state.filters.overview.selectedWidgetLayout,
    useWorkingHoursInCycleTimeDays: state.auth.user.config && state.auth.user.config.useWorkingHoursInCycleTimeDays
  };
};

export default connect(mapStateToProps)(Overview);
